import { InboundDashboard } from '@/components/analytics/dashboards/inbound';
import { OutboundDashboard } from '@/components/analytics/dashboards/outbound';
import { OverviewDashboard } from '@/components/analytics/dashboards/overview';
import { PowerDialerDashboard } from '@/components/analytics/dashboards/power-dialer/power-dialer-dashboard';
import { AlloNumberFilter } from '@/components/analytics/filters/allo-number-filter';
import { ConversionTagFilter } from '@/components/analytics/filters/conversion-tag-filter';
import { type DatePreset, DateRangeFilter } from '@/components/analytics/filters/date-range-filter';
import { EmailFilter } from '@/components/analytics/filters/email-filter';
import type { Granularity } from '@/components/analytics/filters/granularity-filter';
import { useConversionTags } from '@/components/analytics/hooks/use-conversion-tags';
import amplitudeService from '@/services/amplitude';
import { ANALYTICS_EVENTS } from '@/services/analytics';
import { useAnalytics } from '@/services/analytics/use-analytics';
import { createCubeApi } from '@/services/cubejs/cube-client';
import { CubeProvider } from '@cubejs-client/react';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';

import { AnalyticsFeedbackModal } from '@/components/analytics/feedback/analytics-feedback-modal';
import {
  type AnalyticsSearchParams,
  type FilterMode,
  useAnalyticsFilters,
} from '@/components/analytics/hooks/use-analytics-filters';
import { PageContent, PageHeader, PageLayout } from '@/components/ui/design-system';
import { Button } from '@/components/ui/design-system';
import { useAnalyticsAuth } from '@/hooks/use-analytics-auth';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

export const Route = createFileRoute('/_app/analytics')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): AnalyticsSearchParams => {
    return {
      start: typeof search.start === 'string' ? search.start : undefined,
      end: typeof search.end === 'string' ? search.end : undefined,
      preset: (search.preset as DatePreset) || 'today',
      comparison: 'previous_period',
      emails: Array.isArray(search.emails) ? search.emails : undefined,
      allo_numbers: Array.isArray(search.allo_numbers) ? search.allo_numbers : undefined,
      tab: typeof search.tab === 'string' ? search.tab : 'overview',
      filter_mode: (search.filter_mode as FilterMode) || 'user',
      connection_rate_granularity: (search.connection_rate_granularity as Granularity) || 'day',
      conversion_rate_granularity: (search.conversion_rate_granularity as Granularity) || 'day',
    };
  },
});

function RouteComponent() {
  const { t } = useTranslation('desktop_ui');
  const { track } = useAnalytics();
  const { data: authData, isLoading: isAuthLoading, error: authError } = useAnalyticsAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setIsScrolled(e.currentTarget.scrollTop > 0);
  }, []);

  const {
    selectedEmails,
    selectedAlloNumbers,
    activeTab,
    dateRange,
    comparison,
    connectionRateGranularity,
    filterMode,
    setDateRange,
    setConnectionRateGranularity,
    setActiveTab,
    setSelectedEmails,
    setSelectedAlloNumbers,
    setFilterMode,
    allTeamEmails,
    allAlloNumbers,
  } = useAnalyticsFilters();

  const {
    selectedTags: conversionTags,
    availableTags: availableConversionTags,
    setSelectedTags: setConversionTags,
    isLoading: isLoadingConversionTags,
  } = useConversionTags();

  const dateRangeState = useMemo(
    () => ({
      start: dateRange.start,
      end: dateRange.end,
      preset: dateRange.preset,
      comparison,
    }),
    [dateRange.start, dateRange.end, dateRange.preset, comparison],
  );

  const cubeApi = useMemo(() => {
    const token = authData?.data?.token;
    if (!token) {
      return null;
    }
    return createCubeApi(token);
  }, [authData?.data?.token]);

  const tabs = ['overview', 'outbound', 'inbound', 'power_dialer'] as const;

  const resolvedActiveTab = activeTab;

  if (isAuthLoading) {
    return (
      <div className="flex flex-col h-dvh w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="mt-4 text-body-14 text-muted-foreground">
          {t('components.analytics.loading')}
        </p>
      </div>
    );
  }

  if (authError || !cubeApi) {
    return (
      <div className="flex flex-col h-dvh w-full items-center justify-center">
        <p className="text-body-14 text-destructive">{t('components.analytics.loadFailed')}</p>
      </div>
    );
  }

  return (
    <PageLayout>
      <PageHeader
        title={t('components.sidebar.menu.analytics')}
        rightAddOn={
          <Button variant="ghost" size="sm" onClick={() => setFeedbackOpen(true)}>
            {t('components.analytics.feedback.button', 'Send feedback')}
          </Button>
        }
      />
      <PageContent>
        <CubeProvider cubeApi={cubeApi}>
          <div className="flex flex-col h-full w-full py-4">
            <div className="flex flex-1 flex-col overflow-hidden">
              <div
                className={cn(
                  'flex items-center justify-between px-4 pb-3 transition-all border-b',
                  isScrolled ? 'border-primary/8' : 'border-transparent',
                )}
              >
                <div className="flex h-10 items-center gap-4 border-b border-primary-5">
                  {tabs.map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => {
                        setActiveTab(tab);
                        amplitudeService.logEvent('Analytics Dashboard Displayed', {
                          Type: tab.toUpperCase(),
                        });
                      }}
                      className={cn(
                        'flex h-full cursor-pointer items-center py-3',
                        resolvedActiveTab === tab
                          ? 'border-b border-primary-100 text-primary-100'
                          : 'text-primary-60',
                      )}
                    >
                      <span className="text-body-13">
                        {tab === 'power_dialer'
                          ? 'Power dialer'
                          : tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </span>
                    </button>
                  ))}
                </div>
                {(resolvedActiveTab === 'outbound' ||
                  resolvedActiveTab === 'inbound' ||
                  resolvedActiveTab === 'overview' ||
                  resolvedActiveTab === 'power_dialer') && (
                  <div className="flex items-center gap-3">
                    <DateRangeFilter
                      dateRange={dateRange}
                      onDateRangeChange={(newRange) => {
                        setDateRange(newRange);
                        if (resolvedActiveTab === 'overview') {
                          const daysDiff =
                            newRange.start && newRange.end
                              ? Math.round(
                                  (new Date(newRange.end).getTime() -
                                    new Date(newRange.start).getTime()) /
                                    86400000,
                                ) + 1
                              : 0;
                          track(ANALYTICS_EVENTS.ANALYTICS_OVERVIEW.DATE_RANGE_CHANGED, {
                            Preset: newRange.preset,
                            Comparison: comparison,
                            Days: daysDiff,
                          });
                        }
                      }}
                    />
                    {resolvedActiveTab !== 'power_dialer' && (
                      <div className="flex items-center gap-1 bg-primary-5 rounded-full p-0.5">
                        <button
                          type="button"
                          onClick={() => setFilterMode('user')}
                          className={cn(
                            'px-3 py-1 text-body-13 rounded-full transition-all cursor-pointer',
                            filterMode === 'user'
                              ? 'bg-white text-primary-100 shadow-sm'
                              : 'text-primary-100/60 hover:text-primary-100',
                          )}
                        >
                          By User
                        </button>
                        <button
                          type="button"
                          onClick={() => setFilterMode('phone')}
                          className={cn(
                            'px-3 py-1 text-body-13 rounded-full transition-all cursor-pointer',
                            filterMode === 'phone'
                              ? 'bg-white text-primary-100 shadow-sm'
                              : 'text-primary-100/60 hover:text-primary-100',
                          )}
                        >
                          By Phone
                        </button>
                      </div>
                    )}
                    {resolvedActiveTab === 'outbound' && (
                      <ConversionTagFilter
                        selectedTags={conversionTags}
                        availableTags={availableConversionTags}
                        onApply={(tags) => {
                          setConversionTags(tags);
                          amplitudeService.logEvent('Analytics Dashboard Filtered', {
                            'Filter Type': 'conversion_tag',
                            Values: tags.map((tag) => tag.key),
                          });
                        }}
                        isLoading={isLoadingConversionTags}
                      />
                    )}
                    {filterMode === 'user' || resolvedActiveTab === 'power_dialer' ? (
                      <EmailFilter
                        selectedEmails={selectedEmails}
                        availableEmails={allTeamEmails}
                        onApply={(emails) => {
                          setSelectedEmails(emails);
                          amplitudeService.logEvent('Analytics Dashboard Filtered', {
                            'Filter Type': 'email',
                            Values: emails,
                          });
                        }}
                      />
                    ) : (
                      <AlloNumberFilter
                        selectedNumbers={selectedAlloNumbers}
                        availableNumbers={allAlloNumbers}
                        onApply={(numbers) => {
                          setSelectedAlloNumbers(numbers);
                          amplitudeService.logEvent('Analytics Dashboard Filtered', {
                            'Filter Type': 'phone',
                            Values: numbers,
                          });
                        }}
                      />
                    )}
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-auto px-8" onScroll={handleScroll}>
                {resolvedActiveTab === 'outbound' && (
                  <OutboundDashboard
                    selectedEmails={selectedEmails || []}
                    selectedAlloNumbers={selectedAlloNumbers || []}
                    filterMode={filterMode}
                    dateRange={dateRangeState}
                    connectionRateGranularity={connectionRateGranularity}
                    onConnectionRateGranularityChange={setConnectionRateGranularity}
                    conversionTags={conversionTags}
                  />
                )}
                {resolvedActiveTab === 'inbound' && (
                  <InboundDashboard
                    selectedEmails={selectedEmails || []}
                    selectedAlloNumbers={selectedAlloNumbers || []}
                    filterMode={filterMode}
                    dateRange={dateRangeState}
                  />
                )}
                {resolvedActiveTab === 'overview' && (
                  <OverviewDashboard
                    dateRange={dateRangeState}
                    selectedEmails={selectedEmails || []}
                    selectedAlloNumbers={selectedAlloNumbers || []}
                    filterMode={filterMode}
                  />
                )}
                {resolvedActiveTab === 'power_dialer' && (
                  <PowerDialerDashboard
                    selectedEmails={selectedEmails}
                    dateRange={dateRangeState}
                  />
                )}
              </div>
            </div>
          </div>
        </CubeProvider>
        <AnalyticsFeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
      </PageContent>
    </PageLayout>
  );
}
